/**
 * useStockHomologations - Listagem e criação de homologações de rastreador (via RPC).
 *
 * RELEASE: 2026-10-11 v1.1.0 - Plataforma homologada (obrigatória no Aprovado).
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';
import type { HomologationResult, HomologationType } from '@/lib/stock-homologation';

const logger = createLogger('useStockHomologations');

// Preserva o `this` do cliente (RPC fora da tipagem gerada)
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export interface HomologationRow {
  id: string;
  number: string;
  tracker_id: string;
  imei: string;
  esn: string | null;
  model_name: string | null;
  homologation_type: HomologationType;
  result: HomologationResult;
  status_before: string;
  status_after: string;
  notes: string | null;
  created_by_name: string | null;
  /** Plataforma homologada (resultado Aprovado). */
  platform_name?: string | null;
  created_at: string;
}

export interface HomologationFilters {
  search: string;
  type: HomologationType | 'all';
  result: HomologationResult | 'all';
  dateFrom: string | null;
  dateTo: string | null;
  trackerId?: string | null;
  sortField: 'created_at' | 'number' | 'result' | 'homologation_type';
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export function useStockHomologationsList(f: HomologationFilters, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  // Tempo real: novas homologações do tenant recarregam a listagem (canal único)
  useTenantStockRealtime([['stock-homologations']], !!tenantId && enabled);
  return useQuery({
    queryKey: ['stock-homologations', tenantId, f],
    refetchOnWindowFocus: true,
    enabled: !!tenantId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_homologations', {
        p_search: f.search || null,
        p_type: f.type === 'all' ? null : f.type,
        p_result: f.result === 'all' ? null : f.result,
        p_date_from: f.dateFrom,
        p_date_to: f.dateTo,
        p_tracker_id: f.trackerId ?? null,
        p_sort_field: f.sortField,
        p_sort_direction: f.sortDirection,
        p_page: f.page,
        p_page_size: f.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar homologações', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: HomologationRow[]; kpis?: Record<string, number> };
      return { total: p.total ?? 0, items: p.items ?? [], kpis: p.kpis ?? {} };
    },
  });
}

export function useCreateStockHomologation() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { trackerId: string; type: HomologationType; result: HomologationResult; notes: string; platformId?: string | null }) => {
      const { data, error } = await rpc('create_stock_homologation', {
        p_tracker_id: input.trackerId,
        p_type: input.type,
        p_result: input.result,
        p_notes: input.notes.trim().slice(0, 1000) || null,
        p_platform_id: input.platformId ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw new Error(error.message);
      return data as { number: string; status_before: string; status_after: string };
    },
    onSuccess: (r) => {
      // Homologação muda a situação: atualiza listas e fichas do estoque
      ['stock-homologations', 'stock-items', 'stock-movable', 'stock-free', 'stock-overview']
        .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`Homologação ${r.number} registrada`);
    },
    onError: (e: Error) => {
      logger.error('Erro ao registrar homologação', { error: e.message });
      toast.error(e.message || 'Não foi possível registrar a homologação');
    },
  });
}
